import type { ViewDiff, ViewNode } from "pincerjs";
import { byId, h } from "./dom";

/*
Developer mode, for poking at the UI: ?dev in the address, or Alt+Shift+D. Every node shows its compound key and
action on hover, nodes drawn generically (no design yet) are outlined, and a panel lists them with the latest diffs.
In pincerweb the list is the to-do list for designs: change the tree, see what's new, design it.
*/
export class DevMode {

  private on = false;
  private readonly generic = new Set<string>();
  private readonly log: string[] = [];

  constructor() {
    let remembered = false;
    try { remembered = localStorage.getItem("pincer.dev") === "true"; } catch { /* storage can be unavailable */ }
    this.on = new URLSearchParams(location.search).has("dev") || remembered;
    document.addEventListener("keydown", (event) => {
      if (event.altKey && event.shiftKey && event.code === "KeyD") this.toggle();
    });
    this.apply();
  }

  toggle() {
    this.on = !this.on;
    try { localStorage.setItem("pincer.dev", String(this.on)); } catch { /* as above */ }
    this.apply();
  }

  drawn(node: ViewNode, design: string) {
    const element = byId(node.compoundKey);
    if (element !== null) element.title = this.on ? `${node.compoundKey}${node.action ? ` → ${node.action}` : ""} (${design})` : "";
    if (design === "generic") this.generic.add(node.compoundKey); else this.generic.delete(node.compoundKey);
  }

  updated(diff: ViewDiff) {
    for (const key of diff.removed) this.generic.delete(key);
    if (diff.description) this.log.unshift(...diff.description.split("\n").reverse());
    this.log.splice(60);
    if (this.on) this.panel();
  }

  private apply() {
    document.body.classList.toggle("dev", this.on);
    if (this.on) this.panel(); else byId("devPanel")?.remove();
  }

  private panel() {
    let panel = byId("devPanel");
    if (panel === null) {
      panel = h("aside", { id: "devPanel", "aria-label": "Developer mode" });
      document.body.append(panel);
    }
    const generic = [...this.generic].sort();
    panel.replaceChildren(
      h("h2", {}, "Developer mode"),
      h("p", {}, "Alt+Shift+D to close. Hover over anything for its key."),
      h("h3", {}, `Drawn generically (${generic.length})`),
      h("ul", {}, ...generic.slice(0, 200).map((key) => h("li", {}, key))),
      h("h3", {}, "Latest changes"),
      h("pre", {}, this.log.join("\n")),
    );
  }

}
